import React, { useState } from 'react';
import { SignIn } from '@clerk/react';
import { Container, Nav } from 'react-bootstrap';
import { useLocaleContext } from '../i18n';
import type { StoryItem } from '../i18n/types';
import step1 from '../assets/screenshots/step-1.png';
import step2 from '../assets/screenshots/step-2.png';
import step3 from '../assets/screenshots/step-3.png';
import step4 from '../assets/screenshots/step-4.png';
import step5 from '../assets/screenshots/step-5.png';
import LandingHero from './landing/LandingHero';
import LandingLanguageButton from './landing/LandingLanguageButton';
import WalkthroughTab from './landing/WalkthroughTab';
import EcosystemTab from './landing/EcosystemTab';
import ScienceCombinedTab from './landing/ScienceCombinedTab';
import AboutTab from './landing/AboutTab';
import LoreTab from './landing/LoreTab';
import '../styles/landing.css';

const IMGS = [step1, step2, step3, step4, step5];

interface FullStoryItem extends StoryItem {
  img: string;
}

type TabKey = 'walkthrough' | 'why' | 'sticker' | 'integrations' | 'science';

export default function LandingPage() {
  const { locale, toggleLang, lang } = useLocaleContext();
  const [activeTab, setActiveTab] = useState<TabKey>('walkthrough');
  const [openAccordion, setOpenAccordion] = useState<string | null>('walkthrough');

  const toggleAccordion = (key: string) => {
    setOpenAccordion((prev) => {
      const isOpening = prev !== key;
      if (isOpening) {
        setTimeout(() => {
          const element = document.getElementById(`accordion-${key}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      return isOpening ? key : null;
    });
  };

  const story: FullStoryItem[] = locale.storyItems.map((item, i) => ({
    num: i + 1,
    ...item,
    img: IMGS[i],
  }));

  const accordionSections = [
    { key: 'walkthrough' as const, label: locale.tabs.walkthrough, Content: () => <WalkthroughTab story={story} /> },
    {
      key: 'ecosystem' as const,
      label: locale.tabs.ecosystem,
      Content: () => <EcosystemTab sticker={locale.sticker} integrations={locale.integrations} />,
    },
    {
      key: 'science' as const,
      label: locale.tabs.science,
      Content: () => <ScienceCombinedTab why={locale.why} science={locale.science} />,
    },
  ];

  return (
    <div data-bs-theme="dark" className="broomwagon-root">
      <Container fluid="lg" className="broomwagon-container">
        <div className="d-flex d-md-none justify-content-end" style={{ position: 'absolute', top: 32, right: 24, zIndex: 10 }}>
          <LandingLanguageButton lang={lang} toggleLang={toggleLang} />
        </div>
        <div className="broomwagon-columns">
          {/* Left column */}
          <div className="broomwagon-left">
            <LandingHero
              motto={locale.motto}
              hero={locale.hero}
              onLoreClick={() => {
                const loreSection = document.getElementById('lore-section');
                if (loreSection) {
                  const rect = loreSection.getBoundingClientRect();
                  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                  window.scrollTo({
                    top: rect.top + scrollTop - 40,
                    behavior: 'smooth',
                  });
                }
              }}
            />

            {/* Tabs section - desktop only */}
            <div className="broomwagon-tabs-section">
              <Nav
                variant="pills"
                className="mb-3 flex-nowrap"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                }}
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k as TabKey)}
              >
                {accordionSections.map((s) => (
                  <Nav.Item key={s.key}>
                    <Nav.Link eventKey={s.key} style={{ fontSize: '0.85rem', fontWeight: 600, padding: '8px 10px' }}>
                      {s.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <div className="broomwagon-tab-content">{accordionSections.find((s) => s.key === activeTab)?.Content()}</div>
            </div>
          </div>

          {/* Right column - SignIn */}
          <div className="broomwagon-right">
            <div className="d-flex flex-column align-items-center">
              <div style={{ width: '100%', maxWidth: '400px' }}>
                <div className="broomwagon-lang-container d-none d-md-flex">
                  <LandingLanguageButton lang={lang} toggleLang={toggleLang} />
                </div>
                <div className="d-flex justify-content-center">
                  <SignIn
                    appearance={{
                      variables: {
                        colorBackground: '#1a1d20',
                        colorInputBackground: '#23272b',
                        colorText: '#f8f9fa',
                        colorTextSecondary: '#adb5bd',
                        colorPrimary: '#0d6efd',
                        colorInputText: '#f8f9fa',
                        colorNeutral: '#adb5bd',
                      },
                      elements: {
                        header: {
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '16px',
                        },
                        logoBox: {
                          order: 2,
                          justifyContent: 'flex-end',
                          height: '40px',
                          margin: 0,
                        },
                        logoImage: {
                          height: '40px',
                        },
                        headerText: {
                          order: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        },
                        headerTitle: {
                          margin: 0,
                          textAlign: 'left',
                        },
                        headerSubtitle: {
                          textAlign: 'left',
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile accordion */}
        <div className="broomwagon-mobile-accordion" style={{ marginTop: '2rem', paddingBottom: '2rem' }}>
          {accordionSections.map(({ key, label, Content }) => {
            const open = openAccordion === key;
            return (
              <div key={key} id={`accordion-${key}`} style={{ borderTop: '1px solid #2a2a2a' }}>
                <button
                  onClick={() => toggleAccordion(key)}
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '0.85rem 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: open ? '#fff' : '#c4cdd5',
                    fontWeight: open ? 600 : 400,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                  <span style={{ fontSize: '0.75rem', color: '#6c757d', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    ▼
                  </span>
                </button>
                {open && <div style={{ paddingBottom: '1.25rem' }}>{Content()}</div>}
              </div>
            );
          })}
        </div>
      </Container>
      {/* Footer Section */}
      <div style={{ backgroundColor: '#16191c', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 'auto' }}>
        <Container fluid="lg" style={{ paddingTop: '5rem', paddingBottom: '3rem' }}>
          <div className="text-center mb-5">
            <h2 className="text-white fw-bold mb-2" style={{ fontSize: '2rem', letterSpacing: '-0.02em' }}>
              {locale.footerHeading}
            </h2>
            <div style={{ width: '40px', height: '4px', backgroundColor: '#0d6efd', margin: '0 auto', borderRadius: '2px' }}></div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-lg-6">
              <AboutTab about={locale.about} support={locale.support} />
            </div>
            <div className="col-12 col-lg-6" id="lore-section">
              <LoreTab lore={locale.lore} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '3rem' }} />
          <p style={{ marginTop: '1.5rem', fontSize: '0.88rem', color: '#6c757d', textAlign: 'center', lineHeight: 1.6 }}>
            {locale.mobileFootnote}
          </p>
        </Container>
      </div>
    </div>
  );
}
